// lib/client/offlineQueue.js
'use client';

const QUEUE_KEY = 'heart_interactions_queue';
const PROCESS_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Module-level state
let queue = [];
let isProcessing = false;
let processIntervalId = null;

/**
 * Load queue from localStorage
 */
function loadFromStorage() {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (stored) {
      queue = JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load offline queue from storage:', error);
    queue = [];
  }
}

/**
 * Save queue to localStorage
 */
function saveToStorage() {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.warn('Failed to save offline queue to storage:', error);
  }
}

/**
 * Execute a queued operation
 */
async function executeOperation(item) {
  const { fetchJson } = await import('./fetchJson');

  switch (item.type) {
    case 'save':
      await fetchJson(`/api/saved/${item.heartId}`, { method: 'POST' });
      break;
    case 'unsave':
      await fetchJson(`/api/saved/${item.heartId}`, { method: 'DELETE' });
      break;
    case 'visit':
      await fetchJson(`/api/hearts/${item.heartId}/visit`, {
        method: 'POST',
        body: { timestamp: item.timestamp },
      });
      break;
    default:
      throw new Error(`Unknown operation type: ${item.type}`);
  }
}

/**
 * Process queue items
 */
async function processQueue() {
  if (isProcessing || queue.length === 0) return;
  if (!navigator.onLine) return;

  isProcessing = true;
  let processedCount = 0;

  while (queue.length > 0 && navigator.onLine) {
    const item = queue[0];

    try {
      await executeOperation(item);
      // Success - data saved in database, remove from queue
      removeFromQueue(item.id);
      processedCount++;
    } catch (error) {
      // API error - stop processing, keep item in queue for next interval
      console.warn(`Failed to process operation ${item.id}, will retry on next interval:`, error);
      isProcessing = false;
      return;
    }
  }

  // Update cache sync time if any operations were successfully processed
  if (processedCount > 0) {
    try {
      const { updateSyncTime } = await import('./interactionCache');
      updateSyncTime();
    } catch (error) {
      console.warn('Failed to update cache sync time:', error);
    }
  }

  isProcessing = false;
}

/**
 * Remove item from queue
 */
function removeFromQueue(itemId) {
  queue = queue.filter((item) => item.id !== itemId);
  saveToStorage();
}

/**
 * Setup periodic queue processing
 */
function setupPeriodicProcessing() {
  if (typeof window === 'undefined' || processIntervalId !== null) return;

  // Process queue every 5 minutes
  processIntervalId = setInterval(() => {
    processQueue();
  }, PROCESS_INTERVAL_MS);

  // Also process immediately on initialization if online
  if (navigator.onLine) {
    processQueue();
  }
}

/**
 * Setup network listener for immediate sync on connection restore
 */
function setupNetworkListener() {
  if (typeof window === 'undefined') return;

  window.addEventListener('online', () => {
    // Sync immediately when connection is restored
    processQueue();
  });
}

/**
 * Initialize offline queue
 */
function initialize() {
  loadFromStorage();
  setupPeriodicProcessing();
  setupNetworkListener();
}

/**
 * Add operation to queue
 */
export function addToQueue(operation) {
  const queueItem = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    type: operation.type, // 'save', 'unsave', 'visit'
    heartId: parseInt(operation.heartId, 10),
    timestamp: operation.timestamp || new Date().toISOString(),
    createdAt: new Date().toISOString(),
  };

  // Deduplicate: remove existing operations for same heart and type
  queue = queue.filter(
    (item) => !(item.heartId === queueItem.heartId && item.type === queueItem.type)
  );

  queue.push(queueItem);
  saveToStorage();
  // Queue will be processed on the next 5-minute interval
}

/**
 * Clear all queued operations
 */
export function clearQueue() {
  queue = [];
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch (error) {
    console.warn('Failed to clear offline queue from storage:', error);
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initialize();
}
